/**
 * The selected ("active") patient (theme spec 7.3 "Patient context"), as in
 * Dentrix and Eaglesoft: one patient is current for this tab, the Velora
 * Modern top bar (PatientBar.tsx) and the classic shell's patient strip show
 * them, the per-patient modules open on them (patient/follow.ts), and they
 * stay selected when the user switches modules until Unselect.
 *
 * Set by: opening any patient page (`usePublishCurrentPatient`, and
 * SelectedPatientSync for the patient in the URL), the Ctrl K patient
 * search, Select Patient in every mode, and the schedule's right-click
 * "Select patient". Cleared by Unselect (bar button, Escape on the bar,
 * Alt+Shift+X), and by switching to an office whose scope does not hold the
 * patient.
 *
 * Memory only, never browser storage: labels are PHI. It holds the id plus
 * display fields the selecting screen had already fetched; nothing here
 * fetches. Recent patients are kept for this tab's session (the familiar
 * modes also use the server-side list).
 */
import { useEffect, useSyncExternalStore } from 'react';

export interface CurrentPatient {
  id: string;
  /** "Last, First (Pref)"; mask with privacy mode when rendering. Empty while unknown. */
  label: string;
  chartNumber: string | null;
  /** The PMS's own patient number (OD PatNum, Dentrix patid), when synced. */
  pmsId?: string | null;
  /** Age in years, when the selecting screen had it. */
  age?: number | null;
  /** Home office (office scoping); `undefined` when the selecting screen did not know it. */
  homeLocationId?: string | null;
  /** An active patient or medical alert (a badge only; the text is never kept). */
  hasAlert?: boolean;
  /** An active premedication alert (a badge only). */
  premed?: boolean;
}

const RECENT_MAX = 10;
let current: CurrentPatient | null = null;
let recent: readonly CurrentPatient[] = [];
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

const OPTIONAL = ['pmsId', 'age', 'homeLocationId', 'hasAlert', 'premed'] as const;

/**
 * The same patient again: what the new screen knows wins, but an empty label
 * or a missing chart number does not wipe what an earlier screen had.
 */
function merge(prev: CurrentPatient, next: CurrentPatient): CurrentPatient {
  const out: CurrentPatient = {
    ...prev,
    id: next.id,
    label: next.label || prev.label,
    chartNumber: next.chartNumber ?? prev.chartNumber,
  };
  for (const k of OPTIONAL) {
    if (next[k] !== undefined) (out as unknown as Record<string, unknown>)[k] = next[k];
  }
  return out;
}

function same(a: CurrentPatient, b: CurrentPatient): boolean {
  return (
    a.id === b.id &&
    a.label === b.label &&
    a.chartNumber === b.chartNumber &&
    OPTIONAL.every((k) => a[k] === b[k])
  );
}

export function setCurrentPatient(patient: CurrentPatient | null): void {
  if (!patient) {
    if (current === null) return;
    current = null;
    emit();
    return;
  }
  // A new patient known only by id: reuse what this tab already showed of them.
  const base =
    current && current.id === patient.id ? current : recent.find((p) => p.id === patient.id);
  const next = base ? merge(base, patient) : patient;
  if (current && same(current, next)) return;
  current = next;
  if (next.label) {
    recent = [next, ...recent.filter((p) => p.id !== next.id)].slice(0, RECENT_MAX);
  }
  emit();
}

/** Unselect: no patient is current; patient modules open their lists again. */
export function clearCurrentPatient(): void {
  setCurrentPatient(null);
}

export function getCurrentPatient(): CurrentPatient | null {
  return current;
}

export function getRecentPatients(): readonly CurrentPatient[] {
  return recent;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Called after every change of the selected patient (pop-out windows mirror it: windows/WindowSync.tsx). */
export const subscribeCurrentPatient = subscribe;

export function useCurrentPatient(): CurrentPatient | null {
  return useSyncExternalStore(subscribe, getCurrentPatient, getCurrentPatient);
}

export function useRecentPatients(): readonly CurrentPatient[] {
  return useSyncExternalStore(subscribe, getRecentPatients, getRecentPatients);
}

/** Marks `patient` active while the caller shows it (it stays active afterwards). */
export function usePublishCurrentPatient(patient: CurrentPatient | null): void {
  const id = patient?.id ?? null;
  const label = patient?.label ?? null;
  const chart = patient?.chartNumber ?? null;
  const age = patient?.age;
  const home = patient?.homeLocationId;
  const alert = patient?.hasAlert;
  const premed = patient?.premed;
  useEffect(() => {
    if (!id || label === null) return;
    setCurrentPatient({
      id,
      label,
      chartNumber: chart,
      ...(age !== undefined ? { age } : {}),
      ...(home !== undefined ? { homeLocationId: home } : {}),
      ...(alert !== undefined ? { hasAlert: alert } : {}),
      ...(premed !== undefined ? { premed } : {}),
    });
  }, [id, label, chart, age, home, alert, premed]);
}

/**
 * Office scoping: the selected patient stays only while the offices in
 * scope hold them. A patient whose home office is unknown is cleared too
 * (it cannot be shown to be in scope); one with no home office (practice
 * wide) stays.
 */
export function inOfficeScope(
  patient: Pick<CurrentPatient, 'homeLocationId'>,
  locationIds: readonly string[],
): boolean {
  if (patient.homeLocationId === undefined) return false;
  if (patient.homeLocationId === null) return true;
  return locationIds.includes(patient.homeLocationId);
}

/** Test hook. */
export function resetCurrentPatient(): void {
  current = null;
  recent = [];
  emit();
}
